import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

import Portal from 'layout/atoms/Portal';

import styles from './tooltip.module.scss';

type TooltipProps = {
    content: React.ReactNode;
    children: React.ReactNode;
    maxWidth?: number;
    delay?: number;
};

const Tooltip: React.FC<TooltipProps> = ({
    content,
    children,
    maxWidth = 200,
    delay = 200,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const [isBelow, setIsBelow] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isTouchDevice = useRef(false);

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const padding = 8;

        const fitsOnTop = triggerRect.top - tooltipRect.height - padding >= 0;
        const placeBelow = !fitsOnTop;
        setIsBelow(placeBelow);

        const top = placeBelow
            ? triggerRect.bottom + padding
            : triggerRect.top - tooltipRect.height - padding;

        let left =
            triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;

        if (left < padding) {
            left = triggerRect.left;
        } else if (left + tooltipRect.width > window.innerWidth - padding) {
            left = triggerRect.right - tooltipRect.width;
        }

        left = Math.max(
            padding,
            Math.min(left, window.innerWidth - tooltipRect.width - padding)
        );

        setCoords({ top, left });
    }, []);

    const show = useCallback(() => {
        setIsVisible(true);
    }, []);

    const hide = useCallback(() => {
        setIsVisible(false);
    }, []);

    const handleMouseEnter = useCallback(() => {
        if (isTouchDevice.current) return;
        timeoutRef.current = setTimeout(show, delay);
    }, [show, delay]);

    const handleMouseLeave = useCallback(() => {
        if (isTouchDevice.current) return;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        hide();
    }, [hide]);

    const handleTouchStart = useCallback(() => {
        isTouchDevice.current = true;
    }, []);

    const handleClick = useCallback(() => {
        if (!isTouchDevice.current) return;
        setIsVisible((prev) => !prev);
    }, []);

    useEffect(() => {
        if (!isVisible || !isTouchDevice.current) return;

        const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
            if (
                triggerRef.current &&
                !triggerRef.current.contains(e.target as Node) &&
                tooltipRef.current &&
                !tooltipRef.current.contains(e.target as Node)
            ) {
                hide();
            }
        };

        document.addEventListener('touchstart', handleOutsideClick);
        document.addEventListener('mousedown', handleOutsideClick);

        return () => {
            document.removeEventListener('touchstart', handleOutsideClick);
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isVisible, hide]);

    useEffect(() => {
        if (isVisible) {
            requestAnimationFrame(calculatePosition);
        }
    }, [isVisible, calculatePosition]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <>
            <div
                ref={triggerRef}
                className={styles.trigger}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onClick={handleClick}
            >
                {children}
            </div>
            {isVisible && (
                <Portal>
                    <div
                        ref={tooltipRef}
                        className={clsx(
                            styles.tooltip,
                            isBelow ? styles.below : styles.above
                        )}
                        style={{ top: coords.top, left: coords.left, maxWidth }}
                    >
                        {content}
                    </div>
                </Portal>
            )}
        </>
    );
};

export default Tooltip;
